import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, UserPlus, Loader2, KeyRound, Trash2, Copy, ShieldCheck, Eye, ClipboardList } from "lucide-react";

const ROLES = [
  { value: "owner", label: "Brand Owner / Primary Admin", desc: "Full access — manage team, catalogue & everything", icon: ShieldCheck },
  { value: "catalogue", label: "Brand Catalogue User", desc: "Manage products & images only", icon: ClipboardList },
  { value: "viewer", label: "Brand Viewer", desc: "Read-only access", icon: Eye },
];
const ROLE_BADGE = {
  owner: "bg-forest text-cream hover:bg-forest",
  catalogue: "bg-sage/40 text-forest hover:bg-sage/40",
  viewer: "bg-brown/10 text-brown/70 hover:bg-brown/10",
};
const BLANK = { name: "", email: "", brand_role: "catalogue" };

export function BrandTeam() {
  const [members, setMembers] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);
  const [cred, setCred] = useState(null);

  const load = () => api.get("/brand/team").then((r) => setMembers(r.data.members)).catch(() => setMembers([]));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.name.trim()) return toast.error("Enter the team member's name");
    if (!form.email.trim()) return toast.error("Enter an email");
    setSaving(true);
    try {
      const { data } = await api.post("/brand/team", form);
      setInviteOpen(false); setForm({ ...BLANK });
      setCred({ title: "Team member added", email: data.email, password: data.temp_password, login_link: data.login_link });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add member"); }
    finally { setSaving(false); }
  };

  const changeRole = async (m, brand_role) => {
    try { await api.put(`/brand/team/${m.id}`, { brand_role }); toast.success(`${m.name} is now ${ROLES.find((r) => r.value === brand_role)?.label}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update role"); }
  };

  const toggleActive = async (m) => {
    try { await api.put(`/brand/team/${m.id}`, { active: !m.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const resetPw = async (m) => {
    try {
      const { data } = await api.post(`/brand/team/${m.id}/reset-password`);
      setCred({ title: "Password reset", email: data.email, password: data.temp_password, login_link: data.login_link });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset password"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from your team? They will lose access immediately.`)) return;
    try { await api.delete(`/brand/team/${m.id}`); toast.success("Team member removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove member"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="bg-white rounded-2xl soft-shadow p-5" data-testid="brand-team">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h2 className="font-heading text-xl text-forest flex items-center gap-2"><Users className="w-5 h-5 text-olive" /> Team Members</h2>
        <Button onClick={() => { setForm({ ...BLANK }); setInviteOpen(true); }} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="invite-member-btn"><UserPlus className="w-4 h-4 mr-1" /> Invite Member</Button>
      </div>
      <p className="text-sm text-brown/60 mb-4">Add colleagues to your brand portal and control what each of them can do. Team members only ever see your brand's data.</p>

      {!members ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="brand-team-table">
            <thead className="bg-sand text-brown/70 text-xs uppercase tracking-wide">
              <tr><th className="text-left px-3 py-2.5">Member</th><th className="text-left px-3 py-2.5">Role</th><th className="text-left px-3 py-2.5">Status</th><th className="text-right px-3 py-2.5">Actions</th></tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-t border-sage/15" data-testid={`team-member-${m.id}`}>
                  <td className="px-3 py-3">
                    <p className="text-forest font-500">{m.name} {m.is_self && <span className="text-[10px] text-olive">(you)</span>}</p>
                    <p className="text-xs text-brown/60">{m.email}</p>
                    {m.temp_password && <p className="text-[11px] text-earth mt-0.5">Temp password active — awaiting first login</p>}
                  </td>
                  <td className="px-3 py-3">
                    {m.is_primary ? <Badge className={ROLE_BADGE.owner}>Brand Owner / Primary Admin</Badge> : (
                      <Select value={m.brand_role} onValueChange={(v) => changeRole(m, v)}>
                        <SelectTrigger className="h-8 rounded-full w-[210px] bg-sand border-sage/40" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => !m.is_self && !m.is_primary && toggleActive(m)} disabled={m.is_self || m.is_primary} data-testid={`toggle-active-${m.id}`}>
                      <Badge className={m.active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-brown/10 text-brown/50 hover:bg-brown/10"}>{m.active ? "Active" : "Suspended"}</Badge>
                    </button>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <button onClick={() => resetPw(m)} title="Reset password" data-testid={`reset-pw-${m.id}`} className="w-8 h-8 rounded-full border border-sage inline-flex items-center justify-center hover:bg-sage/10"><KeyRound className="w-4 h-4 text-olive" /></button>
                      {!m.is_self && !m.is_primary && <button onClick={() => remove(m)} title="Remove" data-testid={`remove-member-${m.id}`} className="w-8 h-8 rounded-full border border-terracotta/40 inline-flex items-center justify-center hover:bg-terracotta/10"><Trash2 className="w-4 h-4 text-terracotta" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-cream max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">Invite Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm mb-1 block">Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl bg-white" data-testid="invite-name" /></div>
            <div><Label className="text-sm mb-1 block">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl bg-white" data-testid="invite-email" /></div>
            <div>
              <Label className="text-sm mb-1 block">Role *</Label>
              <Select value={form.brand_role} onValueChange={(v) => setForm({ ...form, brand_role: v })}>
                <SelectTrigger className="rounded-xl bg-white" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}><span className="font-500">{r.label}</span></SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-brown/60 mt-1.5">{ROLES.find((r) => r.value === form.brand_role)?.desc}</p>
            </div>
          </div>
          <DialogFooter><Button onClick={invite} disabled={saving} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="invite-submit-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Login"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials dialog */}
      <Dialog open={!!cred} onOpenChange={(o) => !o && setCred(null)}>
        <DialogContent className="bg-cream max-w-md" data-testid="cred-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">{cred?.title}</DialogTitle></DialogHeader>
          {cred && (
            <div className="space-y-3">
              <p className="text-sm text-brown/70">Share these login details securely with the team member. The temporary password must be changed on first login.</p>
              {[["Login Email", cred.email], ["Temporary Password", cred.password], ["Portal Link", cred.login_link]].map(([l, v]) => v && (
                <div key={l} className="bg-white rounded-xl p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0"><p className="text-[10px] uppercase text-olive">{l}</p><p className="text-sm text-forest font-500 truncate">{v}</p></div>
                  <button onClick={() => copy(v)} className="shrink-0 text-olive hover:text-forest"><Copy className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button onClick={() => setCred(null)} className="rounded-full bg-forest text-cream">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
